import styled, { css } from "styled-components";
import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../../constants/design/spacing";
import { foregroundComponent } from "../../../style";
import { OrderbookFilterEnum } from "./shared";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Section = styled.div`
  ${foregroundComponent}
  display: flex;
  flex-direction: column;
  flex: 1;

  &:not(:last-child) {
    margin-bottom: ${SPACING.three}px;
  }
`;

export const Title = styled.div`
  display: flex;
  padding: 0 ${SPACING.two}px 0 0;
  height: ${COMPONENTS.secondaryTabs}px;
  align-items: stretch;
  justify-content: space-between;
  border-bottom: 1px solid ${LAYER_COLORS.three};
`;

export const MediumFilterContainer = styled.div`
  padding: ${SPACING.one}px;
`;

export const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div {
    display: flex;
    align-items: center;
  }
`;

export const TitleFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const FilterComponentHeader = styled.div`
  padding-top: ${SPACING.two}px;
`;

export const DropdownItem = styled.div<{ $selected: boolean }>`
  ${FONT_CSS.header.eight}
  padding: ${SPACING.two}px ${SPACING.two}px;
  color: ${({ $selected }) => ($selected ? TEXT_COLORS.one : TEXT_COLORS.two)};
  /* Keep the selected value and its dropdown chevron on one line. The chevron SVG renders as
     display:block, which pushed it BELOW the value ("0.01" / "BTC") instead of to its right. */
  white-space: nowrap;

  svg {
    display: inline-block;
    vertical-align: middle;
    margin-left: ${SPACING.two}px;
  }
`;

export const ScrollableWrapper = styled.div`
  position: relative;
  flex: 1;
`;

export const ScrollableContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const OrderbookTypeFiltersContainer = styled.div`
  display: flex;

  > * {
    margin-left: ${SPACING.one}px;
  }
`;

export const OrderbookTypeFilterButton = styled.button<{
  isSelected?: boolean;
  filterType?: OrderbookFilterEnum;
}>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  padding: 6px;
  transition: all 0.2s ease-in-out;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? COLORS.neutral.one : "transparent")};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  div {
    background-color: ${COLORS.neutral.three};
    height: 2px;
    border-radius: 2px;
    width: 100%;
  }

  ${({ filterType }) => {
    switch (filterType) {
      case OrderbookFilterEnum.BIDS:
        return css`
          div:nth-child(1) {
          }
          div:nth-child(2) {
          }
          div:nth-child(3) {
            background-color: ${COLORS.up.one};
          }
        `;
      case OrderbookFilterEnum.OFFERS:
        return css`
          div:nth-child(1) {
            background-color: ${COLORS.down.one};
          }
          div:nth-child(2) {
          }
          div:nth-child(3) {
          }
        `;
      default:
        return css`
          div:nth-child(1) {
            background-color: ${COLORS.down.one};
          }
          div:nth-child(3) {
            background-color: ${COLORS.up.one};
          }
        `;
    }
  }}
`;

export const TradeTabWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
`;

export const TradeTab = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ active }) => (active ? COLORS.highlight.one : "transparent")};
  margin-bottom: -1px;
  color: ${({ active }) => (active ? TEXT_COLORS.one : TEXT_COLORS.three)};
  ${FONT_CSS.header.eight}
  padding: 0 ${SPACING.twoHalf}px;
  height: 100%;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;

export const LayoutMenuButton = styled.button`
  background: none;
  border: none;
  padding: ${SPACING.one}px ${SPACING.two}px;
  cursor: pointer;
  color: ${TEXT_COLORS.three};
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: color 0.15s ease;
  flex-shrink: 0;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${SPACING.two}px;
  height: ${COMPONENTS.secondaryTabs}px;
  flex-shrink: 0;
`;

export const SectionHeaderTitle = styled.span`
  ${FONT_CSS.header.eight}
  color: ${TEXT_COLORS.one};
`;

export const StackedContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const StackedOrderbookWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const StackedTradesSeparator = styled.div`
  padding: 0 ${SPACING.two}px;
  height: ${COMPONENTS.secondaryTabs}px;
  display: flex;
  align-items: center;
  border-top: 1px solid ${LAYER_COLORS.three};
  flex-shrink: 0;
`;

export const StackedTradesWrapper = styled.div`
  height: 40%;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: none;
`;

export const LargeContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
  gap: 0;
`;

export const LargeOrderbookColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid ${LAYER_COLORS.three};
`;

export const LargeTradesColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const LayoutDropdownItem = styled.div<{ active?: boolean }>`
  ${FONT_CSS.header.eight}
  padding: ${SPACING.two}px ${SPACING.twoHalf}px;
  color: ${({ active }) => (active ? TEXT_COLORS.one : TEXT_COLORS.three)};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;
